import React, {useContext, useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEdit} from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";

// Contexts
import {SettingsContext} from "../../contexts/settings.js";

// Hooks
import useApi from "../../hooks/useApi.js";
import useMountedState from "../../hooks/useMountedState.js";

// Utils
import {prettyDateInUserTimezone} from "../../utils/helpers.js";

// Components
import Modal from "../../components/Modal.js";
import FilePreview from "../../components/FilePreview.js";

// Style
import {Button, Heading, Inline, Loader, Text} from "../../style/components/general.js";
import voice from "../../style/components/typography.js";
import {pad} from "../../style/components/variables.js";

const ModalFileView = ({
  visible,
  setVisible,
  file,
  expiration,
  updateExpiration,
  date,
  hasBackButton = false,
  goBack = () => {}
}) => {
  const isMounted = useMountedState();

  const {api, loading} = useApi("files");
  const {settings} = useContext(SettingsContext);

  const [selected, setSelected] = useState(file);

  useEffect(() => {
    if (isMounted() && !file.link)
      api.callGet(file.id, {download: true}).then(({status, data}) => {
        if (status === 200) setSelected(data);
      });
  }, [isMounted, file, api]);

  const expirationFormatted = useMemo(
    () =>
      expiration
        ? prettyDateInUserTimezone(expiration, settings?.timezone, "MMM D YYYY")
        : undefined,
    [expiration, settings]
  );

  return (
    <Modal
      visible={visible}
      setVisible={setVisible}
      hasBackButton={hasBackButton}
      goBack={goBack}
      width={file.extension.name.includes("image") ? "min-content" : null}
      maxWidth={file.extension.name === "application/pdf" ? "90%" : null}
      minWidth="50%"
      renderModalControls={
        !expiration && updateExpiration ? (
          <Addition type="button" onClick={() => updateExpiration()} quiet>
            SET EXPIRATION DATE
          </Addition>
        ) : undefined
      }>
      {!loading && selected ? (
        <>
          <Heading>{selected.label}</Heading>
          {date && (
            <Date>
              <strong>ORIGINAL DATE:</strong>&nbsp;
              <NoBreak>{prettyDateInUserTimezone(date, settings?.timezone, "MMM D YYYY")}</NoBreak>
            </Date>
          )}
          <Date>
            <strong>UPLOADED:</strong>{" "}
            <NoBreak>{prettyDateInUserTimezone(file.createdAt, settings?.timezone)}</NoBreak>
            &nbsp; by <NoBreak>{selected.createdBy}</NoBreak>
          </Date>
          {selected.updatedAt && selected.updatedBy && (
            <Date>
              <strong>UPDATED:</strong>&nbsp;
              <NoBreak>
                {prettyDateInUserTimezone(selected.updatedAt, settings?.timezone)}
              </NoBreak>{" "}
              by&nbsp;
              <NoBreak>{selected.updatedBy}</NoBreak>
            </Date>
          )}
          {expirationFormatted && (
            <Inline>
              <Date
                error={dayjs(expirationFormatted).isBefore(
                  dayjs.utc().tz(settings?.timezone),
                  "day"
                )}>
                <strong>EXPIRATION:</strong>&nbsp;
                <NoBreak>{expirationFormatted}</NoBreak>
              </Date>
              {updateExpiration && (
                <Edit type="button" onClick={() => updateExpiration()}>
                  <FontAwesomeIcon icon={faEdit} />
                </Edit>
              )}
            </Inline>
          )}
          <br />

          {selected.link && <FilePreview selected={selected} />}
        </>
      ) : (
        <Loader />
      )}
    </Modal>
  );
};

ModalFileView.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  file: PropTypes.objectOf(PropTypes.any).isRequired,
  date: PropTypes.string,
  expiration: PropTypes.string,
  updateExpiration: PropTypes.func,
  hasBackButton: PropTypes.bool,
  goBack: PropTypes.func
};

const Date = styled(Text)`
  ${voice.weak};
  margin-bottom: ${pad / 3}px;

  ${({error, theme}) =>
    error &&
    css`
      color: ${theme.error};
    `}
`;

const Addition = styled(Button)`
  ${voice.quiet};
  padding: ${pad / 2}px;
  margin-left: ${pad}px;
`;

const NoBreak = styled.span`
  white-space: nowrap;
`;

const Edit = styled.button`
  width: fit-content;
  margin: 5px 0 0 -5px;

  svg {
    fill: ${({theme}) => theme.secondary};
    padding: ${pad / 2}px 0 ${pad}px ${pad}px;
    ${voice.quiet}
  }
`;

export default ModalFileView;
